import type { ITuningEndpoint } from '../contracts/ports';
import type { TuningCapabilities, TuningSettingKey, TuningValue } from '../contracts/settings';
import { isTuningCommand } from '../contracts/identity';
import type { TuningCommand, TuningReceipt, TuningSettingReceipt, TuningTrialSnapshot } from '../contracts/identity';
import type { IGuestTuningAuthority, IGuestTuningState } from './GuestTelemetryPorts';
import { GuestReceiverControlManager } from './GuestReceiverControlManager';
import { GuestTelemetrySource } from './GuestTelemetrySource';

type Options = { state: IGuestTuningState; authority: IGuestTuningAuthority; telemetry: GuestTelemetrySource;
  receivers: GuestReceiverControlManager; capabilities: TuningCapabilities };

/** Serialized guest effects. The owning dispatcher alone commits coordinated revisions. */
export class GuestTuningEndpoint implements ITuningEndpoint {
  private disposed = false;
  private serial: Promise<unknown> = Promise.resolve();
  private readonly receipts = new Map<string, { content: string; receipt: TuningReceipt }>();
  constructor(private readonly deps: Options) {}
  capabilities(): TuningCapabilities {
    const settings = { ...this.deps.capabilities.settings };
    for (const key of ['receiver.videoJitterBufferTargetMs', 'receiver.videoPlayoutDelayHintMs',
      'receiver.audioJitterBufferTargetMs', 'receiver.audioPlayoutDelayHintMs'] as TuningSettingKey[]) settings[key] = this.deps.receivers.support(key);
    settings['telemetry.windowMs'] = { mode: 'live', scope: 'peer', nativeApi: 'supported', values: [250, 500, 1_000] };
    settings['telemetry.statsIntervalMs'] = { mode: 'live', scope: 'peer', nativeApi: 'supported', values: [250, 500, 1_000] };
    settings['telemetry.encodedFrameTap'] = { mode: 'fresh-stream', scope: 'peer', nativeApi: 'unknown' };
    return { ...this.deps.capabilities, settings, armed: !this.disposed && this.deps.capabilities.armed };
  }
  apply(command: TuningCommand): Promise<TuningReceipt> {
    const work = this.serial.then(() => this.applyOnce(command));
    this.serial = work.catch(() => undefined); return work;
  }
  snapshot(): TuningTrialSnapshot { return this.deps.state.snapshot(); }
  dispose(): void { this.disposed = true; this.deps.receivers.dispose(); this.deps.telemetry.dispose(); this.receipts.clear(); }
  private applyOnce(command: TuningCommand): TuningReceipt {
    const receipt: TuningReceipt = { version: 1, commandId: command?.commandId || 'invalid-command', target: { ...this.deps.state.snapshot().target },
      stage: 'rejected', settings: [], policyVersion: this.deps.capabilities.buildId };
    if (this.disposed || !this.deps.capabilities.eligible || !this.deps.capabilities.armed || !isTuningCommand(command)) return { ...receipt, error: { code: 'unavailable', message: 'Guest tuning endpoint is unavailable' } };
    receipt.target = { ...command.target };
    const content = JSON.stringify(command);
    const previous = this.receipts.get(command.commandId);
    if (previous) return previous.content === content ? previous.receipt :
      { ...receipt, error: { code: 'command-conflict', message: 'Command ID was reused with different content' } };
    if (this.receipts.size >= 1_024) return { ...receipt, error: { code: 'command-capacity', message: 'Guest receipt retention limit reached; start a new trial' } };
    const authority = this.deps.authority.validate(command);
    if (!authority.valid) return { ...receipt, error: { code: authority.code, message: authority.message } };
    const target = this.deps.state.snapshot().target;
    if (target.sessionId !== command.target.sessionId || target.trialId !== command.target.trialId ||
      target.peerId !== command.target.peerId || target.connectionEpoch !== command.target.connectionEpoch ||
      target.configRevision !== command.expectedRevision) return { ...receipt, error: { code: 'stale-command', message: 'Guest trial identity or revision changed' } };
    if (command.action.kind === 'stop') {
      receipt.settings = this.deps.receivers.restore(); this.deps.telemetry.arm(false);
      receipt.stage = receipt.settings.some(setting => setting.status === 'failed') ? 'failed' : 'applied';
      if (receipt.stage === 'failed') receipt.error = { code: 'receiver-restore-failed', message: 'One or more production receiver hints could not be restored' };
    } else if (command.action.kind !== 'apply-live') {
      receipt.error = { code: 'requires-fresh-stream', message: 'Lifecycle action belongs to the trial controller' };
    } else {
      const patch = Object.entries(command.action.patch);
      const blocked = patch.filter(([key]) => this.capabilities().settings[key as TuningSettingKey]?.mode !== 'live');
      if (blocked.length) return { ...receipt, settings: blocked.map(([key, value]) => ({ key: key as TuningSettingKey, requested: value,
        status: key === 'telemetry.encodedFrameTap' ? 'requires-fresh-stream' : 'unsupported' })),
        error: { code: 'unsupported-live-patch', message: 'Complete patch must support live application before effects' } };
      const settings = patch.map(([key, value]) => this.applySetting(key as TuningSettingKey, value));
      receipt.settings = settings;
      receipt.stage = settings.some((setting) => ['failed', 'unsupported', 'requires-fresh-stream'].includes(setting.status)) ? 'failed' : 'applied';
    }
    this.receipts.set(command.commandId, { content, receipt });
    return receipt;
  }
  private applySetting(key: TuningSettingKey, value: TuningValue): TuningSettingReceipt {
    if (key.startsWith('receiver.')) return this.deps.receivers.apply(key, value);
    if (key === 'telemetry.encodedFrameTap') return { key, requested: value, status: 'requires-fresh-stream', reason: 'Encoded transform ownership changes on a new receiver' };
    if (key === 'telemetry.windowMs' || key === 'telemetry.statsIntervalMs') {
      this.deps.telemetry.configure(key === 'telemetry.windowMs' ? { windowMs: value as number } : { statsIntervalMs: value as number });
      return { key, requested: value, accepted: value, readback: value, status: 'applied' };
    }
    return { key, requested: value, status: 'unsupported', reason: 'Setting belongs to the host or fresh-stream controller' };
  }
}
